import PropTypes from "prop-types";
import * as React from "react";
import { useInfluencer } from "../../../hooks";
import {
  Button,
  CameraIcon,
  Skeleton,
  TuneIcon,
  Typography,
  VisibilityIcon,
} from "../../../themeV2";
import GlassPill from "../../GlassPill/GlassPill";
import ImageAvatar from "../../ImageAvatar";
import VideoAvatarEditor from "../../VideoAvatarEditor";
import BannerUpload from "./BannerUpload";
import Description from "./Description";
import css from "./ShopHero.module.css";
import ShopStatus from "./ShopStatus";

const ShopBuilderHeader = React.memo(function ShopBuilderHeader({
  route,
  setEditingAvatar,
  setEditingSettings,
}) {
  const { influencer, isLoading, isError, refresh } = useInfluencer({
    route,
    forShopBuilder: true,
  });

  const bannerUrl = influencer?.bannerUrl;

  const editAvatar = React.useCallback(() => {
    setEditingAvatar(true);
  }, [setEditingAvatar]);

  const avatarBlock = (
    <div className={css.avatarRelative}>
      <button
        type="button"
        className={css.shopAvatarCompact}
        onClick={editAvatar}
        aria-label="Edit account photo"
      >
        {influencer?.avatarUrl ? (
          <img src={influencer.avatarUrl} alt={influencer?.name} />
        ) : (
          <ImageAvatar
            disableShadow
            influencer={influencer}
            height={80}
            width={80}
          />
        )}
      </button>
      <GlassPill
        className={css.avatarEditPill}
        onClick={editAvatar}
        aria-label="Edit account photo"
      >
        <CameraIcon />
      </GlassPill>
    </div>
  );

  const nameBlock = (
    <Typography variant="headline-md" as="h2" clamp={2} className={css.name}>
      {influencer?.name ?? <Skeleton style={{ width: 200 }} />}
    </Typography>
  );

  const controlsBlock = (
    <div className={css.controls}>
      <ShopStatus route={route} />

      <Button
        variant="tonal"
        leading={<TuneIcon />}
        onClick={() => setEditingSettings(true)}
      >
        Shop settings
      </Button>

      {influencer?.route && (
        <Button
          as="a"
          variant="filled"
          leading={<VisibilityIcon />}
          href={`/${influencer.route}?preview`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Preview storefront
        </Button>
      )}
    </div>
  );

  return (
    <section className={css.shopHero}>
      {/* ── Banner ── */}
      <div
        className={css.shopBanner}
        style={bannerUrl ? { backgroundImage: `url(${bannerUrl})` } : undefined}
      >
        <div className={css.bannerScrim} />
        {Boolean(influencer) && (
          <BannerUpload
            influencerId={influencer.influencerId}
            bannerUrl={bannerUrl}
            refresh={refresh}
          />
        )}
      </div>

      {/* ── Body ── */}
      <div className={css.shopBodyTwoCol}>
        <div className={css.contentRow}>
          <div className={css.videoColumn}>
            {influencer ? (
              <VideoAvatarEditor influencerId={influencer.influencerId} />
            ) : (
              <Skeleton className={css.videoSkeleton} />
            )}
          </div>
          <div className={css.detailsColumn}>
            <div className={css.titleRow}>
              {avatarBlock}
              {nameBlock}
            </div>
            <Description
              influencer={influencer}
              isLoading={isLoading}
              isError={isError}
              align="start"
            />
          </div>
        </div>
        {controlsBlock}
      </div>
    </section>
  );
});

ShopBuilderHeader.propTypes = {
  route: PropTypes.string,
  setEditingAvatar: PropTypes.func,
  setEditingSettings: PropTypes.func,
};

export default ShopBuilderHeader;
